import { attribute, attributeGetter, Component, type Disposables } from "@bodil/dom/component";
import type { Role } from "aria-attribute-types";
import { html } from "lit";
import { customElement } from "lit/decorators.js";

import { RippleController } from "./controllers/ripple";

import styles from "./button.css?inline";

declare global {
    interface HTMLElementTagNameMap {
        "btk-button": BtkButton;
    }
}

export type BtkButtonVariant =
    | "primary"
    | "neutral"
    | "success"
    | "warning"
    | "danger"
    | "avatar"
    | "overlay"
    | "bare";

export type BtkButtonSize = "small" | "medium" | "large";

@customElement("btk-button")
export class BtkButton extends Component {
    @attribute accessor size: BtkButtonSize | null = null;
    @attribute accessor variant: BtkButtonVariant | null = null;

    @attribute({ type: Boolean }) accessor disabled = false;

    @attributeGetter get role(): Role {
        return "button";
    }

    @attributeGetter({ type: Number, reactive: true }) get tabindex(): number {
        return this.disabled ? -1 : 0;
    }

    private readonly rippleController = new RippleController(this);

    protected override connected(): Disposables {
        this.useWhileConnected(this.on("mousedown", this.handleMouseDown.bind(this)));
    }

    private handleMouseDown(event: MouseEvent): void {
        if (!this.disabled) {
            this.rippleController.playRipple(this, event);
        }
    }

    override click(): void {
        // Disallow programmatic clicks when disabled.
        if (!this.disabled) {
            super.click();
        }
    }

    protected override render(): unknown {
        return html`<slot name="icon"></slot><slot></slot>`;
    }

    static styles = styles;
}
